import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PhoneInTalk } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import svenImage from "src/shared/assets/sven.webp";
import { handleBookCall } from "src/shared/utils";

type Stat = { value: string; label: string };

export const MainInfo: React.FC = () => {
  const { t } = useTranslation();
  const infoMock = t("home.stats", { returnObjects: true }) as Stat[];

  const theme = useTheme();

  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Stack
      width={1}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        gap: { lg: 8, md: 1, sm: 1, xs: 1 },
      }}
    >
      <Stack
        width={1}
        direction="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        gap={1}
      >
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Typography
            width={1}
            variant="h1"
            color="primary.contrastText"
            whiteSpace="nowrap"
            sx={{
              fontSize: {
                xl: "5rem",
                lg: "4.5rem",
                md: "4rem",
                sm: "3rem",
                xs: "2rem",
              },
            }}
          >
            {t("home.welcome")}
            <Typography variant="inherit" fontSize="inherit" color="primary">
              {t("home.name")}
            </Typography>
          </Typography>

          <Box
            component="img"
            width={1}
            height={1}
            src={svenImage}
            alt={t("home.name")}
            sx={{
              display: { sm: "none", xs: "flex" },
              aspectRatio: 1,
              objectFit: "contain",
              overflow: "hidden",
            }}
          />
        </Stack>

        <Typography
          color="text.primary"
          variant="body2"
          sx={{
            fontSize: { lg: "0.875rem", xs: "0.7rem" },
            textAlign: { sm: "left", xs: "center" },
          }}
        >
          {t("home.intro")}
        </Typography>

        <Stack
          width={1}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          {infoMock.map((item, index) => (
            <Stack
              key={index}
              direction="column"
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="h2"
                color="textSecondary"
                sx={{
                  textShadow: "1px 1px 20px rgba(81, 139, 241, 1);",
                  fontSize: {
                    lg: "3.75rem",
                    md: "3rem",
                    sm: "2.5rem",
                    xs: "2rem",
                  },
                }}
              >
                {item.value}
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                whiteSpace="nowrap"
                fontWeight={300}
                sx={{
                  fontSize: {
                    lg: "0.875rem",
                    md: "0.7rem",
                    sm: "0.7rem",
                    xs: "0.6rem",
                  },
                }}
              >
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Button
          variant="outlined"
          color="primary"
          size={isMedium ? "medium" : isSmall ? "small" : "large"}
          startIcon={<PhoneInTalk fontSize="large" color="primary" />}
          onClick={handleBookCall}
          sx={{
            textTransform: "none",
            color: "primary.contrastText",
            gap: 1,
            marginTop: 2,
          }}
        >
          {t("home.bookCall")}
          <Typography color="primary">{t("home.bookCallName")}</Typography>
        </Button>
      </Stack>

      <Box
        component="img"
        width={1}
        height={1}
        src={svenImage}
        alt={t("home.name")}
        sx={{
          display: { sm: "flex", xs: "none" },
          aspectRatio: 1,
          objectFit: "contain",
          overflow: "hidden",
        }}
      />
    </Stack>
  );
};
